import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import type { Ticket } from '../api/types';
import { useDebounced } from '../components/useDebounced';
import { MutationError } from '../components/MutationError';
import { LeadCreateForm } from '../pages/leads/LeadCreateForm';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { noObjectReasons } from './missedCall';

// Verpasster Anruf einer unbekannten Nummer (Dominik 12.10.2026): Mit der Firmennummer telefonieren wir nur aus ErfolgsMakler –
// der Anruf gehört praktisch immer zu einem Objekt. Vorschläge und Suche führen zum Objekt; „Diesem Objekt zuordnen“ speichert die
// Nummer beim Eigentümer und öffnet den Rückruf in der Akquise (über die Firmennummer). Wirklich neu: Lead anlegen; sonst
// „Kein Objekt“ mit Grund.
export function MissedCallPanel({ ticket }: { ticket: Ticket }) {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const callId = ticket.call_id ?? '';
  const caller = ticket.subject.replace(/^Verpasster Anruf von\s*/, '');
  const [save, setSave] = useState(true);
  const [q, setQ] = useState('');
  const [mode, setMode] = useState<'' | 'create' | 'none'>('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const term = useDebounced(q.trim(), 300);
  const suggestions = useQuery({ queryKey: projectKeys.callSuggestions(project.id, callId), queryFn: ({ signal }) => papi.callLeadSuggestions(callId, signal) });
  const search = useQuery({
    queryKey: projectKeys.leads(project.id, { q: term, limit: 8 }),
    queryFn: ({ signal }) => papi.leads({ q: term, limit: 8 }, signal),
    enabled: term.length >= 3,
  });
  const assign = useMutation({
    mutationFn: (leadId: string) => papi.assignCallToLead(callId, { lead_id: leadId, save_number: save }),
    onSuccess: (_, leadId) => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.all(project.id) });
      void navigate(`/p/${project.key}/akquise/${leadId}`);
    },
  });
  const close = useMutation({
    mutationFn: () => papi.updateTicket(ticket.id, { status: 'DONE', note: `Kein Objekt: ${reason}${note.trim() ? ` – ${note.trim()}` : ''}` }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: projectKeys.all(project.id) }),
  });
  if (!can('lead.edit') || !can('call.start')) return null;
  const pick = (id: string) => assign.mutate(id);
  const items = suggestions.data?.items ?? [];
  const found = (search.data?.items ?? []).filter((l) => !items.some((s) => s.lead_id === l.id));
  return (
    <section className="card missed-call" aria-labelledby="missed-call-title">
      <h2 id="missed-call-title">Anruf zuordnen</h2>
      <p className="muted small">
        Zu welchem Objekt gehört {caller}? Danach rufen Sie aus der Akquise über die Firmennummer zurück – der Anruf wird protokolliert.
      </p>
      <label className="checkbox">
        <input type="checkbox" checked={save} onChange={(e) => setSave(e.target.checked)} /> Nummer beim Eigentümer speichern (wird beim nächsten Anruf erkannt)
      </label>
      <h3>Vorschläge</h3>
      {suggestions.isPending ? (
        <p className="muted">Wird gesucht …</p>
      ) : items.length === 0 ? (
        <p className="muted">Keine Vorschläge – bitte suchen oder einen Lead anlegen.</p>
      ) : (
        <ul className="plain-list missed-call-list">
          {items.map((s) => (
            <li key={s.lead_id}>
              <span>
                <strong>{s.name}</strong>
                {s.address && <span className="muted">{s.address}</span>}
                <small className="muted">{s.reason}</small>
              </span>
              <button type="button" className="btn btn-small btn-primary" disabled={assign.isPending} onClick={() => pick(s.lead_id)}>
                Diesem Objekt zuordnen
              </button>
            </li>
          ))}
        </ul>
      )}
      <label className="field wide">
        <span>Anderes Objekt suchen (Name, Adresse, Telefon)</span>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="mindestens 3 Zeichen" />
      </label>
      {term.length >= 3 && (
        <ul className="plain-list missed-call-list">
          {search.isPending ? (
            <li className="muted">Wird gesucht …</li>
          ) : found.length === 0 ? (
            <li className="muted">Nichts gefunden.</li>
          ) : (
            found.map((l) => (
              <li key={l.id}>
                <span>
                  <strong>{l.company_name || [l.first_name, l.last_name].filter(Boolean).join(' ') || 'Lead'}</strong>
                  <span className="muted">
                    {[l.street, l.house_number].filter(Boolean).join(' ')} {[l.postal_code, l.city].filter(Boolean).join(' ')}
                  </span>
                </span>
                <button type="button" className="btn btn-small" disabled={assign.isPending} onClick={() => pick(l.id)}>
                  Diesem Objekt zuordnen
                </button>
              </li>
            ))
          )}
        </ul>
      )}
      {assign.isError && <MutationError error={assign.error} />}
      <div className="form-actions">
        {can('lead.create') && (
          <button type="button" className="btn" onClick={() => setMode(mode === 'create' ? '' : 'create')}>
            Neuer Eigentümer – Lead anlegen
          </button>
        )}
        <button type="button" className="btn btn-ghost" onClick={() => setMode(mode === 'none' ? '' : 'none')}>
          Kein Objekt
        </button>
      </div>
      {mode === 'create' && <LeadCreateForm phone={caller} onCreated={(lead) => pick(lead.id)} onCancel={() => setMode('')} />}
      {mode === 'none' && (
        <form
          className="inline-form"
          onSubmit={(e) => {
            e.preventDefault();
            close.mutate();
          }}
        >
          <label className="field">
            <span>Grund</span>
            <select value={reason} onChange={(e) => setReason(e.target.value)} required>
              <option value="">Bitte wählen</option>
              {noObjectReasons.map((r) => (
                <option key={r}>{r}</option>
              ))}
            </select>
          </label>
          <label className="field wide">
            <span>Hinweis (optional)</span>
            <input value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
          </label>
          <button type="submit" className="btn btn-primary" disabled={!reason || close.isPending}>
            Erledigt – kein Objekt
          </button>
          {close.isError && <MutationError error={close.error} />}
        </form>
      )}
    </section>
  );
}
